//
// Device common util
// -----------

define(function(require, exports, module) {
    'use strict';

    const _ = require('underscore');
    const deviceList = require('util/device.list');
    const { getUserAgent } = require('bundle');

    var device = {
        /**
         * @type {String}  --  user agent in lower case
         */
        get ua() {
            return getUserAgent();
        },

        /**
         * @type {String}  --  user agent in origin format
         */
        get uaOrigin() {
            return getUserAgent(true);
        },

        /**
         * Get array of browser name and version
         * @private
         * @return {Array}
         */
        get _possibleUserAgents() {
            return this.ua.match(/(chrome|crios|firefox|fxios|safari|msie|trident(?=\/))\/?\s*(\d+)/) || [];
        },

        /**
         * Search matches in user agent
         * @param  {String} needle  --  search item
         * @return {Boolean}
         */
        _find: function(needle) {
            return this.ua.indexOf(needle) > -1;
        },

        /**
         * @type {String}  --  operating system ['ios', 'android', 'windows', 'macintosh']
         */
        get os() {
            var appleList = ['iphone', 'ipod', 'ipad'];
            var osList = ['android', 'windows', 'macintosh', 'windows phone'];
            var os = null;

            if (this._find('windows phone')) {
                return 'windowsphone';
            }

            if (_.find(appleList, this._find, this) || this.isIpadLikeDesktop) {
                return 'ios';
            }

            os = _.find(osList, this._find, this);

            return os ? os.replace(/ /g, '') : '';
        },

        get osVersion() {
            var version = null;
            var windowsVersions = {
                '10.': '10',
                '6.4': '10',
                '6.3': '8.1',
                '6.2': '8',
                '6.1': '7',
                '6.0': 'Vista',
                '5.2': 'XP 64-bit',
                '5.1': 'XP'
            };

            switch (this.os) {
                case 'windows':
                    version = windowsVersions[this.ua.split('windows nt ')[1]?.slice(0, 3)];
                    break;
                case 'macintosh':
                    version = this.ua.split('os ')[1]?.slice(0, 5).replace(/_/g, '.').split(' ')[0];
                    break;
                case 'ios':
                    version = this.ua.split('os ')[1]?.slice(0, 5).replace(/_/g, '.').split(' ')[0];
                    break;
                case 'android':
                    version = this.ua.split(/android([\s\/])*/i)[2]?.slice(0, 5).replace(/_/g, '.').split(' ')[0];
                    version = /^[0-9\.]*$/.test(version) ? version : parseFloat(version);
                    break;
                case 'windowsphone':
                    version = this.ua.split('windows phone ')[1]?.slice(0, 4);
                    version = parseFloat(version);
                    break;
            }

            return version;
        },

        get isCrawlerBot() {
            return /bot|google|baidu|bing|msn|duckduckgo|teoma|slurp|yandex/i.test(this.ua) || window.isCrawler;
        },

        get isSamsungChromeBrowser() {
            return /SamsungBrowser\/([\d.]+)/i.exec(this.ua);
        },

        get samsungChromeBrowserVersion() {
            var matched = this.ua.match(/(samsungbrowser(?=\/))\/?\s*(\d+)/i);

            return matched && matched[2];
        },

        /**
         * @type {Boolean}  --  is os from golden list
         */
        get isOtherOs() {
            return !_.contains(['android', 'ios', 'windows', 'macintosh'], this.os);
        },

        /**
         * @type {String}  --  device name ['iphone', 'androidPhone', ...]
         */
        get name() {
            var os = this.os;
            var name = null;
            var uaGroups = deviceList.uaGroups;

            if (os === 'ios') {
                name = ['iphone', 'ipad', 'ipod'].find(this._find.bind(this)) || (this.isIpadLikeDesktop ? 'ipad' : '');
            } else if (os === 'android') {
                // uc Browser in userAgent always writes Phone for both tablet and phone
                if (this.browserName === 'uc') {
                    for (var formFactor in uaGroups) {
                        for (var deviceAlias in uaGroups[formFactor]) {
                            if (uaGroups[formFactor][deviceAlias].test(this.ua)) {
                                name = os + (formFactor === 'phones' ? 'Phone' : 'Tablet');
                            }
                        }
                    }
                } else {
                    name = os + (this._find('mobile') || this._find('lenovo-a') ? 'Phone' : 'Tablet');
                }
            } else if (os === 'windows') {
                name = os + (this._find('phone') ? 'Phone' : '');
            } else if (os === 'windowsphone') {
                name = 'windowsPhone';
            }

            return name;
        },

        /**
         * @type {String}  --  form-factor ['desktop', 'tablet', 'mobile']
         */
        get formFactor() {
            var formFactor = null;

            if (_.contains(['iphone', 'ipod', 'androidPhone', 'windowsPhone'], this.name)) {
                formFactor = 'mobile';
            } else if (_.contains(['ipad', 'androidTablet'], this.name) || this.isIpadLikeDesktop) {
                formFactor = 'tablet';
            } else {
                formFactor = 'desktop';
            }

            return formFactor;
        },

        get alias() {
            var list = deviceList.ua;
            var alias = 'other';

            for (var deviceAlias in list) {
                if (list[deviceAlias].test(this.ua)) {
                    alias = deviceAlias;
                }
            }

            if (alias === 'other') {
                list = deviceList.apple[this.name];

                for (var apple in list) {
                    if (list[apple] === Math.max(screen.width, screen.height)) {
                        alias = apple;
                    }
                }
                if (alias === 'ipad' && window.devicePixelRatio === 2) {
                    alias = 'ipad_retina';
                }
                // iPhone6 & iPhone7 has the same resolution & userAgent, need to distinguish it before sending on IMS
                if (alias === 'iphone_6' && parseInt(this.osVersion) >= 10) {
                    alias = 'iphone_6_7';
                }
                if (alias === 'iphone_6_plus' && parseInt(this.osVersion) >= 10) {
                    alias = 'iphone_6_7_plus';
                }
            }

            return alias;
        },

        /**
         * @type {String}  --  orientation ['portrait', 'landscape']
         */
        get orientation() {
            let landscapeMode;
            const isScreenOrientationSupported = 'orientation' in screen && typeof screen.orientation.type === 'string'; // iOS NOT supported
            const isWindowOrient = 'orientation' in window; // Deprecated! For iOS, Opera for Android & Samsung Internet
            const isLandscape = angle => angle === null
                ? screen.availHeight < screen.availWidth
                : Boolean(angle / 90 % 2);

            if (isScreenOrientationSupported) {
                landscapeMode = screen.orientation.type.includes('landscape');
            } else {
                const angle = isWindowOrient ? window.orientation : null;
                landscapeMode = isLandscape(angle);
            }

            return landscapeMode ? 'landscape' : 'portrait';
        },

        /**
         * Detected real screen width and height
         * @return {Object}  {width: @integer, height: @integer}
         */
        get realScreenDimensions() {
            return {
                width: Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
                height: Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
            };
        },

        get screen() {
            return {
                width: screen.width,
                height: screen.height
            };
        },

        get body() {
            return {
                height: Math.max(document.body.clientHeight, device.realScreenDimensions.height)
            };
        },

        /**
         * Real pixel ratio
         * @type {Number}
         */
        pixelRatio: window.devicePixelRatio || 1,

        /**
         * Check is device in portrait
         * @return {Boolean}
         */
        isPortrait: function() {
            return this.orientation === 'portrait';
        },

        /**
         * Check is device in landscape
         * @return {Boolean}
         */
        isLandscape: function() {
            return this.orientation === 'landscape';
        },

        /**
         * @type {String}  --  browser name
         */
        get browserName() {
            var browser = this._possibleUserAgents[1] || null;

            // iOS standalone mode
            if (!browser && this.os === 'ios') {
                browser = 'safari';
            }

            if (browser === 'crios') {
                browser = 'chrome';
            }

            if (browser === 'fxios') {
                return 'firefox';
            }

            // Android native browser
            var resultChromeRegEx = /Chrome\/([\d.]+)/i.exec(this.ua);
            var chromeVersion = resultChromeRegEx === null ? null : parseFloat(resultChromeRegEx[1]);

            if ((!browser || browser === 'safari') && this.os === 'android' || (
                // native android browser for android with chrome web view
                chromeVersion != null && chromeVersion < 37 || this.isSamsungChromeBrowser
            )) {
                browser = 'androidNative';
            }

            // Opera
            if (this.ua.includes('opr')) {
                browser = 'opera';
            }

            // Edge
            // Possible UA identifier tokens for Edge on Chromium: Edg, EdgiOS, EdgA
            if (this.ua.includes('edg')) {
                browser = 'edge';
            }

            // UC
            if (this.ua.includes('ucbrowser')) {
                browser = 'uc';
            }

            return browser === 'msie' || browser === 'trident' ? 'ie' : browser;
        },

        /**
         * Check version of browser
         * @return {String}
         */
        get browserVersion() {
            var re = /(opera|chrome|crios|safari|firefox|fxios|msie|trident(?=\/))\/?\s*(\d+)/i;
            var edgeRe = /\b(edge|edg|edga|edgeios)\/(\d+)/i;
            var ucRe = /\b(ucbrowser)\/([\d.]+)/i;
            var matched = this.ua.match(ucRe) || this.ua.match(edgeRe) || this.ua.match(re) || [];
            var item;

            // applewebkit could not be joined with regexp above because it will be always the first match
            matched = matched.length ? matched : this.ua.match(/(applewebkit(?=\/))\/?\s*(\d+)/i) || [];

            matched = matched[2] ? [matched[1], matched[2]] : [navigator.appName, navigator.appVersion, '-?'];

            // 'safari/' part at UA-s means kernel version - 535-537...
            // so real version in all browsers goes after its name(Chrome/28), and only in safari - after "Version/"
            if ((item = this.ua.match(/version\/(\d+)/i)) != null && this.browserName === 'safari') {
                matched.splice(1, 1, item[1]);
            }

            if (!this.ua.includes('msie') && this.ua.includes('trident') && this.ua.includes('rv:11')) {
                matched[1] = '11';
            }

            return matched[1];
        },

        /**
         * @type {Boolean}  --  is browser from golden list
         */
        get isOtherBrowser() {
            return !_.contains(['chrome', 'ie', 'firefox', 'safari'], this.browserName);
        },

        // android 4 galaxies native: impossible to hide URL bar
        // android 5 galaxies native: delta is not changing
        navBarProblematicDeviceList: [
            'galaxy_note3',
            'galaxy_s2',
            'galaxy_s3',
            'galaxy_s4',
            'galaxy_s5',
            'galaxy_s3_mini',
            'galaxy_s4_mini',
            'Lenovo_A850'
        ],

        get isNavBarProblematic() {
            return this.os === 'windowsphone'
                || this.browserName !== 'uc'
                && this.browserName !== 'chrome'
                && this.navBarProblematicDeviceList.includes(this.alias);
        },

        supportedBrowsersVersions: {
            ie: 9,
            edge: 1,
            chrome: 25,
            safari: 5,
            firefox: 20
        },

        isSupportedBrowser: function() {
            var browser = this.browserName;
            var version = parseInt(this.browserVersion);
            var isSupported = false;

            // List of supported browsers
            switch (browser) {
                case 'ie':
                    isSupported = version >= this.supportedBrowsersVersions.ie;
                    break;
                case 'uc':
                    isSupported = true;
                    break;
                case 'edge':
                    isSupported = version >= this.supportedBrowsersVersions.edge;
                    break;
                case 'chrome':
                    isSupported = !this.ua.includes('yabrowser') && version >= this.supportedBrowsersVersions.chrome;
                    break;
                case 'safari':
                    isSupported = version >= this.supportedBrowsersVersions.safari;
                    break;
                case 'firefox':
                    isSupported = version >= this.supportedBrowsersVersions.firefox;
                    break;
                case 'opera':
                    isSupported = false;
                    break;
                case 'androidNative':
                    isSupported = true;
                    break;
            }

            return isSupported;
        },

        /**
         * Check if device support standalone mode
         */
        get isStandaloneMode() {
            return Boolean(window.navigator.standalone);
        },

        /**
         * @type {Object}  --  base info about device
         */
        get info() {
            return {
                os: this.os,
                deviceName: this.name,
                formFactor: this.formFactor
            };
        },

        /**
         * Detect if device keyboard is open
         * @return {Boolean}
         */
        get isKeyboardPresent() {
            return document.hasFocus()
                && ['input', 'select', 'textarea'].includes(document.activeElement.tagName.toLowerCase());
        },

        get browserLanguage() {
            return !_.isEmpty(navigator.languages)
                ? navigator.languages[0]
                : navigator.language || navigator.userLanguage;
        },

        get isTouchDevice() {
            return 'ontouchstart' in document.documentElement;
        },

        /**
         * After iOS 13 all iPads have useragent like on Safari desktop
         * @returns {Boolean}
         */
        get isIpadLikeDesktop() {
            return this.isTouchDevice && this.ua.includes('macintosh');
        }
    };

    module.exports = device;
});
